import type {FC} from 'react'
import {ActionList, IconButton} from '@primer/react'
import {DownloadIcon, GraphIcon, HistoryIcon, PulseIcon, TrashIcon} from '@primer/octicons-react'
import {Link} from '@github-ui/react-router'
import {downloadRuleset} from '../helpers/export-ruleset'
import type {Ruleset} from '@github-ui/repos-rules-types/rules-types'
import {RulesetEnforcement} from '@github-ui/repos-rules-types/rules-types'
import {useRelativeNavigation} from '../hooks/use-relative-navigation'
import {useIsStafftools} from '../hooks/use-is-stafftools'
import {verifiedFetch} from '@github-ui/verified-fetch'
import sudo from '@github-ui/sudo'
import type {FlashAlert} from '@github-ui/dismissible-flash'
import {useRuleStrings} from '../hooks/use-rule-strings'
import {useUpsellInfoContext} from '../contexts/UpsellInfoContext'

export const RulesetActionMenu: FC<{
  ruleset: Ruleset
  rulesetsUrl: string
  isRestored?: boolean
  showDeleteAction: boolean
  readOnly?: boolean
  rulesHistory: boolean
  rulesImportExport: boolean
  setFlashAlert: (flashAlert: FlashAlert) => void
  setMenuOpen: (menuOpen: boolean) => void
  setShowDeleteDialog2: (showDeleteDialog2: boolean) => void
}> = ({
  ruleset,
  rulesetsUrl,
  isRestored,
  showDeleteAction,
  readOnly,
  rulesHistory,
  rulesImportExport,
  setFlashAlert,
  setMenuOpen,
  setShowDeleteDialog2,
}) => {
  const isStafftools = useIsStafftools()
  const {enterpriseRulesets} = useUpsellInfoContext()
  const viewHistory =
    rulesHistory && (isStafftools || (!isStafftools && !readOnly && enterpriseRulesets.featureEnabled))
  const {rulesetOrPolicy} = useRuleStrings()

  return (
    <ActionList>
      {viewHistory && (
        <ActionList.LinkItem
          as={Link}
          aria-label={`View history for the ${rulesetOrPolicy} named ${ruleset.name}`}
          to={`${rulesetsUrl}/${ruleset.id}/history`}
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          className="text-decoration-skip"
        >
          <ActionList.LeadingVisual>
            <HistoryIcon size={16} />
          </ActionList.LeadingVisual>
          History
        </ActionList.LinkItem>
      )}
      <InsightsLink
        ruleset={ruleset}
        rulesetsUrl={rulesetsUrl}
        readOnly={readOnly}
        rulesHistory={rulesHistory}
        rulesImportExport={rulesImportExport}
      />
      {rulesImportExport && (!readOnly || isStafftools) && (
        <ActionList.Item
          onSelect={async () => {
            if (isRestored) {
              setMenuOpen(false)
              // To reduce confusion on what is being exported during restoration,
              // don't allow exporting a ruleset while restoring.
              setFlashAlert({
                variant: 'danger',
                message: `Cannot export a ${rulesetOrPolicy} while restoring. Save or cancel and try again.`,
              })
            } else {
              try {
                await downloadRuleset(`${rulesetsUrl}/${ruleset.id}/export_ruleset`, ruleset.name)
              } catch {
                setMenuOpen(false)
                setFlashAlert({
                  variant: 'danger',
                  message: `Error exporting ${rulesetOrPolicy}`,
                })
              }
            }
          }}
        >
          <ActionList.LeadingVisual>
            <DownloadIcon size={16} />
          </ActionList.LeadingVisual>
          Export {rulesetOrPolicy}
        </ActionList.Item>
      )}
      {!readOnly && !isStafftools && (
        <DeleteAction
          isRestored={isRestored}
          showDeleteAction={showDeleteAction}
          setFlashAlert={setFlashAlert}
          setMenuOpen={setMenuOpen}
          setShowDeleteDialog2={setShowDeleteDialog2}
        />
      )}
    </ActionList>
  )
}

export const InsightsLink: FC<{
  ruleset: Ruleset
  rulesetsUrl: string
  readOnly?: boolean
  rulesHistory: boolean
  rulesImportExport: boolean
}> = ({ruleset, rulesetsUrl, readOnly, rulesHistory, rulesImportExport}) => {
  const key = ruleset.source.type
  const isStafftools = useIsStafftools()
  const {rulesetOrPolicy} = useRuleStrings()
  const {enterpriseRulesets} = useUpsellInfoContext()

  if (
    (!readOnly || isStafftools) &&
    ruleset.enforcement !== RulesetEnforcement.Disabled &&
    key === 'Repository' &&
    enterpriseRulesets.featureEnabled
  ) {
    return rulesHistory || rulesImportExport ? (
      <ActionList.LinkItem
        as={Link}
        aria-label={`View insights for the ${rulesetOrPolicy} named ${ruleset.name}`}
        to={`${rulesetsUrl}/insights?ruleset=${ruleset.name}`}
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        className="text-decoration-skip"
      >
        <ActionList.LeadingVisual>
          <GraphIcon size={16} />
        </ActionList.LeadingVisual>
        Insights
      </ActionList.LinkItem>
    ) : (
      <IconButton
        as={Link}
        aria-label={`View insights for the ${rulesetOrPolicy} named ${ruleset.name}`}
        variant="invisible"
        icon={PulseIcon}
        to={`insights?ruleset=${ruleset.name}`}
        reloadDocument
      />
    )
  }

  return null
}

export const deleteRuleset = async (
  deletePath: string,
  redirectPath: string | undefined,
  setFlashAlert: (flashAlert: FlashAlert) => void,
  setMenuOpen: (menuOpen: boolean) => void,
  setShowDeleteDialog: (showDeleteDialog: boolean) => void,
  rulesetOrPolicy: string,
  navigate: ReturnType<typeof useRelativeNavigation>['navigate'],
) => {
  setShowDeleteDialog(false)
  if (!(await sudo())) {
    setMenuOpen(false)
    setFlashAlert({
      variant: 'danger',
      message: 'Unauthorized',
    })
    return
  }

  try {
    const res = await verifiedFetch(deletePath, {
      method: 'delete',
    })
    if (res.ok) {
      if (redirectPath) navigate(redirectPath, undefined, true, true)
    } else {
      setMenuOpen(false)
      setFlashAlert({
        variant: 'danger',
        message: `Error deleting ${rulesetOrPolicy}`,
      })
    }
  } catch {
    setMenuOpen(false)
    setFlashAlert({
      variant: 'danger',
      message: `Error deleting ${rulesetOrPolicy}`,
    })
  }
}

const DeleteAction: FC<{
  isRestored?: boolean
  showDeleteAction: boolean
  setFlashAlert: (flashAlert: FlashAlert) => void
  setMenuOpen: (menuOpen: boolean) => void
  setShowDeleteDialog2: (showDeleteDialog2: boolean) => void
}> = ({isRestored, showDeleteAction, setFlashAlert, setMenuOpen, setShowDeleteDialog2}) => {
  const {rulesetOrPolicy} = useRuleStrings()

  return showDeleteAction ? (
    <ActionList.Item
      variant="danger"
      onSelect={e => {
        if (isRestored) {
          setMenuOpen(false)
          // To reduce confusion on what is being deleted during restoration,
          // don't allow deleting a ruleset while restoring.
          setFlashAlert({
            variant: 'danger',
            message: `Cannot delete a ${rulesetOrPolicy} while restoring. Save or cancel and try again.`,
          })
        } else {
          setShowDeleteDialog2(true)
        }
        e.preventDefault()
      }}
    >
      <ActionList.LeadingVisual>
        <TrashIcon size={16} />
      </ActionList.LeadingVisual>
      Delete {rulesetOrPolicy}
    </ActionList.Item>
  ) : null
}
